import { put, call, select, takeEvery, take, race } from 'redux-saga/effects';
import get from 'lodash/get';
import includes from 'lodash/includes';
import { notification } from 'antd';
import { push } from 'connected-react-router';

import * as actions from './actions';
import { fromAuth } from '../selectors';
import urlTransform from '../../services/ant-redux/urlTransform';
import { keysToNumbers } from '../../containers/forecast/Forecast';

export function* createResource(api, { needle, data }, { resource, thunk }) {
  try {
    const { count, response } = yield call(
      [api, api.post],
      `/${urlTransform(resource, needle)}`,
      data
    );
    const detail = yield response;

    if (resource === 'forecast' && detail.id && detail.revision_id) {
      // https://inmarket.atlassian.net/browse/IM-2474
      yield put(push(`/forecasts/${detail.id}/revision/${detail.revision_id}`));
    }

    yield put(actions.resourceCreateSuccess(resource, detail, { data, needle }, thunk, count));
  } catch (e) {
    let errorMessage = e;
    if (e && e.response) {
      errorMessage = yield e.response.json();
      errorMessage = get(errorMessage, 'error', errorMessage);
    }

    if (typeof window.FS === 'function') {
      window.FS.log('error', errorMessage);
    }

    if (get(e, 'response.status') === 403) {
      localStorage.removeItem('user');
      return yield put(push('/login'));
    }

    yield put(actions.resourceCreateFailure(resource, errorMessage, { data, needle }, thunk));
  }
}

export function* readResourceList(api, { params, needle }, { resource, thunk }) {
  try {
    const { count, response } = yield call([api, api.get], `/${urlTransform(resource, needle)}`, {
      params,
    });
    const list = yield response;

    yield put(actions.resourceListReadSuccess(resource, list, { params, needle }, thunk, count));
  } catch (e) {
    let errorMessage = e;
    if (get(e, 'response')) {
      errorMessage = yield e.response.json();
      errorMessage = get(errorMessage, 'error', errorMessage);
    }

    if (typeof window.FS === 'function') {
      window.FS.log('error', errorMessage);
    }

    if (get(e, 'response.status') === 403) {
      localStorage.removeItem('user');
      return yield put(push('/login'));
    }

    yield put(actions.resourceListReadFailure(resource, errorMessage, { params, needle }, thunk));
  }
}

export function* readResourceDetail(api, { params, needle }, { resource, thunk }) {
  try {
    const { count, response } = yield call([api, api.get], `/${urlTransform(resource, needle)}`, {
      params,
    });
    const detail = yield response;

    yield put(
      actions.resourceDetailReadSuccess(resource, detail, { params, needle }, thunk, count)
    );
  } catch (e) {
    let errorMessage = e;
    if (e && e.response) {
      errorMessage = yield e.response.json();
      errorMessage = get(errorMessage, 'error', errorMessage);
    }
    if (typeof window.FS === 'function') {
      window.FS.log('error', errorMessage);
    }

    if (get(e, 'response.status') === 403) {
      localStorage.removeItem('user');
      return yield put(push('/login'));
    }

    yield put(actions.resourceDetailReadFailure(resource, errorMessage, { params, needle }, thunk));
  }
}

export function* updateResource(api, { needle, data }, { resource, thunk }) {
  try {
    const { count, response } = yield call(
      [api, api.put],
      `/${urlTransform(resource, needle)}`,
      data
    );
    const detail = yield response;

    yield put(actions.resourceUpdateSuccess(resource, detail, { needle, data }, thunk, count));
  } catch (e) {
    let errorMessage = e;
    if (e && e.response) {
      errorMessage = yield e.response.json();
      errorMessage = get(errorMessage, 'error', errorMessage);
    }
    if (typeof window.FS === 'function') {
      window.FS.log('error', errorMessage);
    }

    if (get(e, 'response.status') === 403) {
      localStorage.removeItem('user');
      return yield put(push('/login'));
    }

    yield put(actions.resourceUpdateFailure(resource, errorMessage, { needle, data }, thunk));
  }
}

export function* deleteResource(api, { needle }, { resource, thunk }) {
  try {
    const { count } = yield call([api, api.delete], `/${urlTransform(resource, needle)}`);
    yield put(actions.resourceDeleteSuccess(resource, { needle }, thunk, count));
  } catch (e) {
    let errorMessage = e;
    if (e && e.response) {
      errorMessage = yield e.response.json();
      errorMessage = get(errorMessage, 'error', errorMessage);
    }
    if (typeof window.FS === 'function') {
      window.FS.log('error', errorMessage);
    }

    if (get(e, 'response.status') === 403) {
      localStorage.removeItem('user');
      return yield put(push('/login'));
    }

    yield put(actions.resourceDeleteFailure(resource, errorMessage, { needle }, thunk));
  }
}

const isRevisionTarget = meta => {
  const resource = get(meta, 'resource', '');
  const targets = ['app', 'chain', 'state', 'location'];

  const hasResource = includes(resource, 'revision');
  const hasTarget = targets.some(v => resource.indexOf(v) >= 0);

  return hasResource && hasTarget;
};

// When user updates targeting on a published revision
// Update the currently loaded revision (as above), but change status_id to 0 before making request
// Then, do the targeting POST request
function* saveRevision(payload) {
  const state = yield select();
  const forecast = get(state, 'form.Forecast.values');
  const forecastResults = get(state, 'form.ForecastResults.values');
  const forecastAdjustments = get(state, 'form.ForecastAdjustments.values');

  delete forecastResults.audience;

  if (forecast.status !== 0) {
    // Force status id to be 0 when adding targeting
    const data = {
      ...keysToNumbers(forecast),
      ...keysToNumbers(forecastResults),
      ...keysToNumbers(forecastAdjustments),
      status: 0,
    };

    const forecastId = get(payload, 'needle.forecastId', get(payload, 'needle.id'));
    const revisionId = get(
      payload,
      'needle.revisionId',
      get(payload, 'needle.itemId', get(payload, 'needle.id'))
    );

    yield put(
      actions.resourceUpdateRequest(
        'forecast/:forecastId/revision/:id',
        { id: revisionId, forecastId },
        data
      )
    );

    const response = yield race({
      error: take(actions.RESOURCE_UPDATE_FAILURE),
      success: take(actions.RESOURCE_UPDATE_SUCCESS),
    });

    // Don't add / delete this target because the API request failed
    if (response.error) {
      notification.error({
        message: 'Error',
        description: `Could not update revision. Targeting will not be updated.`,
        duration: 0,
      });

      return false;
    }
  }

  return true;
}

export function* watchResourceCreateRequest(api, { payload, meta }) {
  if (isRevisionTarget(meta)) {
    const success = yield call(saveRevision, payload);
    if (!success) return;
  }

  yield call(createResource, api, payload, meta);
}

export function* watchResourceListReadRequest(api, { payload, meta }) {
  yield call(readResourceList, api, payload, meta);
}

export function* watchResourceDetailReadRequest(api, { payload, meta }) {
  yield call(readResourceDetail, api, payload, meta);
}

export function* watchResourceUpdateRequest(api, { payload, meta }) {
  yield call(updateResource, api, payload, meta);
}

export function* watchResourceDeleteRequest(api, { payload, meta }) {
  if (isRevisionTarget(meta)) {
    const success = yield call(saveRevision, payload);
    if (!success) return;
  }

  yield call(deleteResource, api, payload, meta);
}

export default function*({ api }) {
  const token = yield select(fromAuth.getToken);
  if (token) {
    api.setToken(token);
  }

  yield takeEvery(actions.RESOURCE_CREATE_REQUEST, watchResourceCreateRequest, api);
  yield takeEvery(actions.RESOURCE_LIST_READ_REQUEST, watchResourceListReadRequest, api);
  yield takeEvery(actions.RESOURCE_DETAIL_READ_REQUEST, watchResourceDetailReadRequest, api);
  yield takeEvery(actions.RESOURCE_UPDATE_REQUEST, watchResourceUpdateRequest, api);
  yield takeEvery(actions.RESOURCE_DELETE_REQUEST, watchResourceDeleteRequest, api);
}
